/**
 * Zod validation for the IPAWS origination config form (ADR-139 D4).
 *
 * Mirrors the backend `IPAWSConfigSerializer` constraints so the form surfaces
 * the same errors client-side before the round-trip:
 *   - `cog_id` required, ≤32 chars (model `max_length=32`)
 *   - `certificate_secret_name` required, ≤255 chars, and must be a Secrets
 *     Manager secret NAME — anything containing a PEM marker is rejected
 *     (defense-in-depth twin of the API guard; ADR-098 Tier 3 custody)
 *   - `enabled_channels` a subset of the IPAWS channels
 *   - event codes: non-empty alert-type names mapped to SAME event codes
 *
 * The form edits `default_event_codes` as an array of rows (name/code pairs);
 * `eventCodeRowsToMap` / `eventCodeMapToRows` convert to and from the wire
 * shape (a `Record<string, string>`).
 */

import { z } from 'zod';
import { IPAWS_CHANNELS } from '../services/ipaws.service';

/** PEM marker the backend rejects in `certificate_secret_name`. */
const PEM_MARKER = '-----BEGIN';

/**
 * SAME event codes are three-letter codes (e.g. CEM, FRW, EVI). Input is
 * accepted case-insensitively; `eventCodeRowsToMap` upper-cases on submit.
 */
const SAME_EVENT_CODE_PATTERN = /^[A-Za-z]{3}$/;

export const ipawsEventCodeRowSchema = z.object({
  alertType: z.string().trim().min(1, 'Alert-type name is required.'),
  eventCode: z
    .string()
    .trim()
    .regex(
      SAME_EVENT_CODE_PATTERN,
      'SAME event codes are three letters (e.g. CEM, FRW, EVI).',
    ),
});

export const ipawsConfigFormSchema = z.object({
  is_enabled: z.boolean(),
  cog_id: z
    .string()
    .trim()
    .min(1, 'COG ID is required.')
    .max(32, 'COG ID must be 32 characters or fewer.'),
  certificate_secret_name: z
    .string()
    .trim()
    .min(1, 'Certificate secret name is required.')
    .max(255, 'Secret name must be 255 characters or fewer.')
    .refine((value) => !value.includes(PEM_MARKER), {
      message:
        'This field stores the AWS Secrets Manager secret NAME, never certificate or key material.',
    }),
  enabled_channels: z.array(z.enum(IPAWS_CHANNELS)),
  event_code_rows: z
    .array(ipawsEventCodeRowSchema)
    .superRefine((rows, ctx) => {
      const seen = new Set<string>();
      rows.forEach((row, index) => {
        const key = row.alertType.trim().toLowerCase();
        if (!key) return; // row-level min(1) already reports this
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            message: `Duplicate alert-type name "${row.alertType.trim()}".`,
            path: [index, 'alertType'],
          });
        }
        seen.add(key);
      });
    }),
});

export type IpawsConfigFormValues = z.infer<typeof ipawsConfigFormSchema>;
export type IpawsEventCodeRow = z.infer<typeof ipawsEventCodeRowSchema>;

/** Form rows → wire shape (`default_event_codes` mapping). */
export function eventCodeRowsToMap(
  rows: IpawsEventCodeRow[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const row of rows) {
    const name = row.alertType.trim();
    const code = row.eventCode.trim().toUpperCase();
    if (name && code) {
      map[name] = code;
    }
  }
  return map;
}

/** Wire shape → form rows (stable order for the field array). */
export function eventCodeMapToRows(
  map: Record<string, string> | null | undefined,
): IpawsEventCodeRow[] {
  return Object.entries(map ?? {}).map(([alertType, eventCode]) => ({
    alertType,
    eventCode: String(eventCode ?? ''),
  }));
}
